import { Link } from 'react-router-dom';
import { useScrollAnimation, useParallax } from '@/hooks/useScrollAnimation';

const features = [
  { icon: 'ri-24-hours-line', text: 'Atención médica permanente, las 24 horas' },
  { icon: 'ri-hospital-line', text: 'Más de 15 especialidades disponibles' },
  { icon: 'ri-microscope-line', text: 'Tecnología moderna y de vanguardia' },
  { icon: 'ri-hand-heart-line', text: 'Consultorios gratuitos para especialistas' },
];

export default function AboutSection() {
  const { ref, isVisible } = useScrollAnimation();
  const smallImg = useParallax(-0.1);

  return (
    <section className="py-20 md:py-28 px-4 md:px-8 lg:px-12 bg-background-50 relative overflow-hidden">
      <div className="absolute top-20 left-0 w-64 h-64 bg-accent-100/40 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left: layered images — 5 cols */}
          <div ref={ref} className={`lg:col-span-5 relative reveal-left ${isVisible ? 'is-in' : ''}`}>
            <div className="relative">
              {/* Big image */}
              <div className="rounded-[2rem] overflow-hidden border-4 border-background-50 w-[86%]">
                <img
                  src="https://static.readdy.ai/image/5774bb29582c40f5252e7d37d3c89ae3/5ffc415b0c6b5998bcc18c756456648b.png"
                  alt="Fundadores y equipo de Clínica Tarija Vitalmedic SRL"
                  className="w-full h-[300px] md:h-[440px] object-cover object-top"
                />
              </div>

              {/* Small overlapping image */}
              <div
                ref={smallImg.ref}
                className="absolute -bottom-8 right-0 w-[52%] rounded-2xl overflow-hidden border-4 border-background-50"
                style={{ transform: `translateY(${smallImg.offset}px)` }}
              >
                <img
                  src="https://static.readdy.ai/image/5774bb29582c40f5252e7d37d3c89ae3/f53a02f12d709b868683438b16a57827.png"
                  alt="Equipo de enfermería de Vitalmedic"
                  className="w-full h-[160px] md:h-[220px] object-cover object-top"
                />
              </div>

              {/* Floating stat badge */}
              <div className="absolute -top-5 -left-3 md:-left-6 bg-accent-500 rounded-2xl px-5 py-4 text-center">
                <p className="font-heading text-2xl md:text-3xl font-bold text-secondary-950 leading-none">+500</p>
                <p className="text-[11px] text-secondary-900 mt-1 font-medium">Consultas / mes</p>
              </div>

              {/* dotted deco */}
              <div className="absolute -bottom-4 -left-4 w-24 h-24 texture-dots -z-10" />
            </div>
          </div>

          {/* Right: text — 7 cols */}
          <div className={`lg:col-span-7 reveal-right ${isVisible ? 'is-in' : ''}`}>
            <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] text-primary-500 uppercase mb-4">
              <span className="w-8 h-px bg-primary-400" />
              Sobre Nosotros
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-medium text-foreground-950 mb-6 leading-[1.08]">
              Comprometidos con la salud de <span className="italic text-primary-600">Tarija</span>
            </h2>
            <p className="text-foreground-600 leading-relaxed mb-8 max-w-xl">
              Clínica Tarija Vitalmedic SRL nació con la misión de brindar atención médica de calidad a toda la comunidad. Un equipo multidisciplinario, instalaciones modernas y atención humana las 24 horas.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-9">
              {features.map((feature) => (
                <div key={feature.text} className="flex items-start gap-3 p-4 rounded-xl bg-background-100 border border-background-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 flex-shrink-0">
                    <i className={`${feature.icon} text-base`} />
                  </div>
                  <span className="text-sm text-foreground-700 leading-snug pt-1">{feature.text}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
              <Link
                to="/nosotros"
                className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                Conocer nuestra historia
                <span className="w-4 h-4 flex items-center justify-center group-hover:translate-x-1 transition-transform">
                  <i className="ri-arrow-right-line" />
                </span>
              </Link>
              <a
                href="tel:+59177173439"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-background-300 text-foreground-800 text-sm font-semibold hover:border-primary-400 hover:text-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-line" />
                </span>
                77173439
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}